import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { merge } from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import { Impressionable } from '@fiverr-private/impressionable';
import Wrapper from '../Wrapper';
import constant from './constants';

/**
 * Impressions wrapper for components
 * @param WrappedComponent - since we're using HOC reference, it can't be stateless
 * @see {@link https://reactjs.org/docs/components-and-props.html#stateless-functions}
 * @param config
 * @return wrapped component
 * **/
const withImpressions = (WrappedComponent, config) => {
    class WithImpressions extends PureComponent {
        constructor(props) {
            super(props);

            this.getImpressionContainer = this.getImpressionContainer.bind(this);
            this.enrichData = this.enrichData.bind(this);
            this.collectImpressionable = this.collectImpressionable.bind(this);
        }

        componentDidMount() {
            this.createImpressionable();
        }

        componentDidUpdate() {
            this.createImpressionable();
        }

        createImpressionable() {
            const { DOMAttribute, DOMSelector } = config;

            this.impressionable = new Impressionable({
                DOMSelector,
                DOMAttribute,
                container: this.getImpressionContainer(),
                enrich: this.enrichData,
            });
        }

        collectImpressionable() {
            if (this.impressionable.unimpressed.length > 0) {
                this.impressionable.collect();
            }
        }

        enrichData(data) {
            const { gigId, userId, studioId, eventSource, pageCtxId, pageName } = this.props;

            const { DEFAULT_IMPRESSION } = constant;

            const { getImpressionData } = config;

            const localization = {};

            try {
                const { currency, locale } = getContext();
                localization.display_currency = currency;
                localization.formatting_locale = locale;
            } catch (err) {
                /** ignored */
            }

            const impressionData = getImpressionData(data, this.props);

            return merge(
                {},
                {
                    ...DEFAULT_IMPRESSION,
                    gig: {
                        id: gigId,
                    },
                    seller: {
                        id: userId,
                    },
                    studio: {
                        id: studioId,
                    },
                    event_source: eventSource,
                    page: {
                        ctx_id: pageCtxId,
                        name: pageName,
                    },
                    out_of_sessions: false,
                    localization,
                },
                impressionData
            );
        }

        // TODO: ReactDOM.findDOMNode should be removed once the package updates to React16
        getImpressionContainer() {
            // eslint-disable-next-line react/no-find-dom-node
            return ReactDOM.findDOMNode(this.containerRef);
        }

        getWrapperClassName() {
            const { DOMSelector } = config;
            const className = DOMSelector?.replace(/\./g, '');
            return className ? `${className}-wrapper` : 'wrapper';
        }

        render() {
            return (
                <Wrapper className={this.getWrapperClassName()} ref={(el) => (this.containerRef = el)}>
                    <WrappedComponent
                        {...this.props}
                        collectImpressionable={this.collectImpressionable}
                        getContainerRef={this.getImpressionContainer}
                    />
                </Wrapper>
            );
        }
    }

    const mapStateToProps = ({ pageCtxId, pageName, studio_id, user_id, gig_id, event_source, ...rest }) => ({
        pageCtxId,
        pageName,
        studioId: studio_id,
        eventSource: event_source,
        userId: user_id,
        gigId: gig_id,
        ...rest,
    });

    return connect(mapStateToProps)(WithImpressions);
};

export default withImpressions;
